import * as THREE from 'three';
import React from 'react';
import { animationController } from './UnifiedAnimationController';
import { resolveMorphTargetIndex } from '../converter';

export type VoiceNonSpeechMode = 'idle' | 'listening' | 'thinking' | 'speaking';

export interface VoiceNonSpeechAnimationController {
  setMode: (mode: VoiceNonSpeechMode) => void;
  getMode: () => VoiceNonSpeechMode;
  cleanup: () => void;
}

interface CapturedPose {
  headX: number;
  headY: number;
  headZ: number;
  neckX: number;
  neckY: number;
  neckZ: number;
  leftEyeX: number;
  leftEyeY: number;
  rightEyeX: number;
  rightEyeY: number;
}

// Morph targets for visible listening/thinking expressions
interface MorphOverlay {
  [arkitName: string]: number;
}

// Morph overlay weights mirror the subtler Expression Gallery variants:
// listening-v2 ("Attentive") and thinking-v4 ("Processing"). Head/eye motion
// carries the state; face morphs should not force a large grin or grimace.
const LISTENING_MORPHS: MorphOverlay = {
  browInnerUp: 0.25,
  eyeWideLeft: 0.10,
  eyeWideRight: 0.10,
  mouthSmileLeft: 0.10,
  mouthSmileRight: 0.10,
};

const THINKING_MORPHS: MorphOverlay = {
  browDownLeft: 0.15,
  browDownRight: 0.15,
  eyeSquintLeft: 0.15,
  eyeSquintRight: 0.15,
};

export function setupVoiceNonSpeechAnimation(
  headRef: React.RefObject<THREE.Object3D>,
  neckRef: React.RefObject<THREE.Object3D>,
  leftEyeRef?: React.RefObject<THREE.Object3D>,
  rightEyeRef?: React.RefObject<THREE.Object3D>,
  morphMesh?: THREE.SkinnedMesh | null,
  morphDict?: Record<string, number> | null,
): VoiceNonSpeechAnimationController | null {
  if (!headRef.current || !neckRef.current) {
    console.warn('Missing refs for unified voice non-speech animation');
    return null;
  }

  let rafId: number | null = null;
  let isDisposed = false;
  let currentMode: VoiceNonSpeechMode = 'idle';
  let speakingHoldPose: CapturedPose | null = null;
  const motionSeed = Math.random() * Math.PI * 2;

  // Pre-resolve morph indices for performance
  const resolvedListening: Array<[number, number]> = [];
  const resolvedThinking: Array<[number, number]> = [];
  if (morphMesh?.morphTargetInfluences && morphDict) {
    for (const [key, val] of Object.entries(LISTENING_MORPHS)) {
      const idx = resolveMorphTargetIndex(morphDict, key);
      if (idx !== undefined) resolvedListening.push([idx, val]);
    }
    for (const [key, val] of Object.entries(THINKING_MORPHS)) {
      const idx = resolveMorphTargetIndex(morphDict, key);
      if (idx !== undefined) resolvedThinking.push([idx, val]);
    }
  }
  const hasMorphSupport = resolvedListening.length > 0 || resolvedThinking.length > 0;

  // Current morph influence targets (smoothly interpolated) and the overlay
  // value already applied to each morph. We apply deltas against the previous
  // overlay so the expression does not accumulate every frame.
  const currentMorphValues = new Map<number, number>();
  const appliedMorphValues = new Map<number, number>();

  const capturePose = (): CapturedPose => ({
    headX: headRef.current?.rotation.x || 0,
    headY: headRef.current?.rotation.y || 0,
    headZ: headRef.current?.rotation.z || 0,
    neckX: neckRef.current?.rotation.x || 0,
    neckY: neckRef.current?.rotation.y || 0,
    neckZ: neckRef.current?.rotation.z || 0,
    leftEyeX: leftEyeRef?.current?.rotation.x || 0,
    leftEyeY: leftEyeRef?.current?.rotation.y || 0,
    rightEyeX: rightEyeRef?.current?.rotation.x || 0,
    rightEyeY: rightEyeRef?.current?.rotation.y || 0,
  });

  const initialPose = capturePose();

  const applyPose = (target: CapturedPose, alpha: number) => {
    if (!headRef.current || !neckRef.current) {
      return;
    }

    headRef.current.rotation.x = THREE.MathUtils.lerp(headRef.current.rotation.x, target.headX, alpha);
    headRef.current.rotation.y = THREE.MathUtils.lerp(headRef.current.rotation.y, target.headY, alpha);
    headRef.current.rotation.z = THREE.MathUtils.lerp(headRef.current.rotation.z, target.headZ, alpha);

    neckRef.current.rotation.x = THREE.MathUtils.lerp(neckRef.current.rotation.x, target.neckX, alpha);
    neckRef.current.rotation.y = THREE.MathUtils.lerp(neckRef.current.rotation.y, target.neckY, alpha);
    neckRef.current.rotation.z = THREE.MathUtils.lerp(neckRef.current.rotation.z, target.neckZ, alpha);

    if (leftEyeRef?.current) {
      leftEyeRef.current.rotation.x = THREE.MathUtils.lerp(leftEyeRef.current.rotation.x, target.leftEyeX, alpha);
      leftEyeRef.current.rotation.y = THREE.MathUtils.lerp(leftEyeRef.current.rotation.y, target.leftEyeY, alpha);
    }

    if (rightEyeRef?.current) {
      rightEyeRef.current.rotation.x = THREE.MathUtils.lerp(rightEyeRef.current.rotation.x, target.rightEyeX, alpha);
      rightEyeRef.current.rotation.y = THREE.MathUtils.lerp(rightEyeRef.current.rotation.y, target.rightEyeY, alpha);
    }
  };

  const applyMorphOverlay = (alpha: number) => {
    if (!hasMorphSupport || !morphMesh?.morphTargetInfluences) return;

    const targets = currentMode === 'listening' ? resolvedListening
      : currentMode === 'thinking' ? resolvedThinking
      : [];

    // Add gentle pulsing to listening morphs so the avatar looks alive
    const now = performance.now() / 1000;
    const pulse = currentMode === 'listening'
      ? 0.85 + Math.sin(now * 1.4 + motionSeed) * 0.15
      : 1.0;

    const targetMap = new Map<number, number>();
    for (const [idx, val] of targets) {
      targetMap.set(idx, val * pulse);
    }
    const influences = morphMesh.morphTargetInfluences;

    // Smoothly interpolate all tracked morph indices toward their target
    const allIndices: number[] = [];
    currentMorphValues.forEach((_v, k) => { if (!allIndices.includes(k)) allIndices.push(k); });
    targetMap.forEach((_v, k) => { if (!allIndices.includes(k)) allIndices.push(k); });
    for (let i = 0; i < allIndices.length; i++) {
      const idx = allIndices[i];
      const target = targetMap.get(idx) ?? 0;
      const current = currentMorphValues.get(idx) ?? 0;
      const next = current + (target - current) * alpha;

      if (Math.abs(next) < 0.001 && target === 0) {
        currentMorphValues.delete(idx);
      } else {
        currentMorphValues.set(idx, next);
      }

      if (idx < influences.length) {
        const previousOverlay = appliedMorphValues.get(idx) ?? 0;
        influences[idx] = Math.max(0, Math.min(1, influences[idx] - previousOverlay + next));
        if (Math.abs(next) < 0.001 && target === 0) {
          appliedMorphValues.delete(idx);
        } else {
          appliedMorphValues.set(idx, next);
        }
      }
    }
  };

  const clearMorphOverlay = () => {
    if (!hasMorphSupport || !morphMesh?.morphTargetInfluences) return;

    const influences = morphMesh.morphTargetInfluences;
    appliedMorphValues.forEach((value, idx) => {
      if (idx < influences.length) {
        influences[idx] = Math.max(0, Math.min(1, influences[idx] - value));
      }
    });
    appliedMorphValues.clear();
    currentMorphValues.clear();
  };

  const getTargetPose = (nowMs: number): CapturedPose => {
    const time = nowMs / 1000;

    if (currentMode === 'speaking' && speakingHoldPose) {
      return speakingHoldPose;
    }

    if (currentMode === 'listening') {
      const focusAmount = 0.012;
      const eyeDriftY = Math.sin(time * 1.1 + motionSeed) * 0.003;
      const eyeDriftX = Math.sin(time * 0.9 + motionSeed * 0.7) * 0.0025;
      const headTilt = Math.sin(time * 0.45 + motionSeed) * 0.024;
      // Gentle micro-nod oscillation to appear attentive
      const microNod = Math.sin(time * 1.1 + motionSeed * 0.5) * 0.0045;
      return {
        ...initialPose,
        headX: initialPose.headX + 0.014 + microNod,
        headY: initialPose.headY - 0.018,
        headZ: initialPose.headZ + headTilt + 0.012,
        neckX: initialPose.neckX + 0.012,
        neckY: initialPose.neckY - 0.008,
        leftEyeX: initialPose.leftEyeX + eyeDriftX,
        leftEyeY: initialPose.leftEyeY + focusAmount + eyeDriftY,
        rightEyeX: initialPose.rightEyeX + eyeDriftX * 0.9,
        rightEyeY: initialPose.rightEyeY - focusAmount + eyeDriftY * 0.95,
      };
    }

    if (currentMode === 'thinking') {
      const lookDown = -0.035;
      const lookAway = 0.038;
      const wanderX = Math.sin(time * 0.45 + motionSeed) * 0.005;
      const wanderY = Math.cos(time * 0.35 + motionSeed * 0.6) * 0.006;
      const headDrift = Math.sin(time * 0.3 + motionSeed * 0.4) * 0.045;
      const headTilt = Math.cos(time * 0.25 + motionSeed * 0.8) * 0.015;
      return {
        ...initialPose,
        headX: initialPose.headX - 0.035,
        headY: initialPose.headY + headDrift + 0.030,
        headZ: initialPose.headZ + headTilt + 0.018,
        neckY: initialPose.neckY + headDrift * 0.4,
        leftEyeX: initialPose.leftEyeX + lookDown + wanderX,
        leftEyeY: initialPose.leftEyeY + lookAway + wanderY,
        rightEyeX: initialPose.rightEyeX + lookDown + wanderX * 0.9,
        rightEyeY: initialPose.rightEyeY + lookAway + wanderY * 0.85,
      };
    }

    const idleBreath = Math.sin(time * 0.42 + motionSeed) * 0.018;
    const idleSway = Math.sin(time * 0.24 + motionSeed * 0.7) * 0.028;
    const idleTilt = Math.cos(time * 0.31 + motionSeed * 0.5) * 0.012;
    const idleEyeDriftX = Math.sin(time * 0.62 + motionSeed * 0.3) * 0.002;
    const idleEyeDriftY = Math.cos(time * 0.54 + motionSeed * 0.9) * 0.0025;

    return {
      ...initialPose,
      headX: initialPose.headX + idleBreath * 0.7 + idleTilt,
      headY: initialPose.headY + idleSway,
      headZ: initialPose.headZ + idleTilt * 0.4,
      neckX: initialPose.neckX + idleBreath * 0.28,
      neckY: initialPose.neckY + idleSway * 0.36,
      neckZ: initialPose.neckZ + idleTilt * 0.18,
      leftEyeX: initialPose.leftEyeX + idleEyeDriftX,
      leftEyeY: initialPose.leftEyeY + idleEyeDriftY,
      rightEyeX: initialPose.rightEyeX + idleEyeDriftX * 0.9,
      rightEyeY: initialPose.rightEyeY + idleEyeDriftY * 0.92,
    };
  };

  const animate = (now: number) => {
    if (rafId !== null) {
      animationController.untrackRafId(rafId);
      rafId = null;
    }

    if (isDisposed) {
      return;
    }

    const targetPose = getTargetPose(now);
    const alpha = currentMode === 'speaking' ? 0.28 : 0.20;
    applyPose(targetPose, alpha);
    applyMorphOverlay(alpha);

    rafId = requestAnimationFrame(animate);
    animationController.trackRafId(rafId);
  };

  rafId = requestAnimationFrame(animate);
  animationController.trackRafId(rafId);

  return {
    setMode(mode: VoiceNonSpeechMode) {
      currentMode = mode;
      if (mode === 'speaking') {
        speakingHoldPose = capturePose();
      } else {
        speakingHoldPose = null;
      }
    },
    getMode() {
      return currentMode;
    },
    cleanup() {
      isDisposed = true;
      if (rafId !== null) {
        animationController.untrackRafId(rafId);
        rafId = null;
      }
      if (headRef.current && neckRef.current) {
        headRef.current.rotation.set(initialPose.headX, initialPose.headY, initialPose.headZ);
        neckRef.current.rotation.set(initialPose.neckX, initialPose.neckY, initialPose.neckZ);
      }
      if (leftEyeRef?.current) {
        leftEyeRef.current.rotation.set(initialPose.leftEyeX, initialPose.leftEyeY, 0);
      }
      if (rightEyeRef?.current) {
        rightEyeRef.current.rotation.set(initialPose.rightEyeX, initialPose.rightEyeY, 0);
      }
      clearMorphOverlay();
    },
  };
}
